import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useToast } from '@/shared/components/Toast';
import { getToken, setToken, setUnauthenticatedHandler } from '@/shared/services/http';
import * as authService from '@/modules/auth/services/auth.service';
import type { AuthenticatedOutcome, Profile } from '@/modules/auth/models/auth.model';

export type AuthStatus = 'checking' | 'authenticated' | 'anonymous';

interface AuthContextValue {
  status: AuthStatus;
  user: Profile | null;
  /** Registra a sessão recém-criada. */
  signIn: (outcome: AuthenticatedOutcome) => void;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth precisa estar dentro de <AuthProvider>.');
  return context;
}

/**
 * Estado da sessão.
 *
 * Ao carregar a aplicação, um token guardado não é aceito de imediato: é
 * confirmado em `/auth/me`. O servidor resolve o usuário no banco a cada
 * requisição, então um token de conta desativada — ou revogada por logout em
 * outro dispositivo — é recusado ali, e não depois, no meio de uma tela.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const toast = useToast();
  const [status, setStatus] = useState<AuthStatus>(() => (getToken() ? 'checking' : 'anonymous'));
  const [user, setUser] = useState<Profile | null>(null);
  const expirouRef = useRef(false);

  const signIn = useCallback((outcome: AuthenticatedOutcome) => {
    setToken(outcome.token);
    setUser(outcome.user);
    setStatus('authenticated');
    expirouRef.current = false;
  }, []);

  const signOut = useCallback(async () => {
    await authService.logout();
    setUser(null);
    setStatus('anonymous');
  }, []);

  // Qualquer 401 vindo da API encerra a sessão local, venha de onde vier.
  useEffect(() => {
    setUnauthenticatedHandler(() => {
      setUser(null);
      setStatus((current) => {
        // O aviso só aparece para quem estava dentro: numa tela de login, um 401
        // é a resposta esperada, não uma sessão perdida.
        if (current === 'authenticated' && !expirouRef.current) {
          expirouRef.current = true;
          toast.show({
            tone: 'warning',
            message: 'Sua sessão expirou',
            description: 'Entre novamente para continuar.',
          });
        }
        return 'anonymous';
      });
    });

    return () => setUnauthenticatedHandler(() => {});
  }, [toast]);

  useEffect(() => {
    if (status !== 'checking') return;

    let cancelado = false;
    authService
      .me()
      .then((perfil) => {
        if (cancelado) return;
        setUser(perfil);
        setStatus('authenticated');
      })
      .catch(() => {
        if (cancelado) return;
        setToken(null);
        setUser(null);
        setStatus('anonymous');
      });

    return () => {
      cancelado = true;
    };
  }, [status]);

  const value = useMemo(() => ({ status, user, signIn, signOut }), [status, user, signIn, signOut]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
